import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { DEPARTMENTS, getDeptLabel } from '@/constants/departments';
import ManagerTeamAttendanceCard from '@/components/manager/ManagerTeamAttendanceCard';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import EmployeeAvatar from '@/components/EmployeeAvatar';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Users,
  Clock,
  CalendarCheck,
  CalendarX,
  Money,
  House,
  Globe,
  SignOut,
  CaretDown,
  ClockClockwise,
  FileText,
  UserCircle,
  GearSix,
  Scales,
  Bell,
  Buildings,
  Receipt,
  Briefcase,
  Bank,
  ShieldWarning,
  TreeStructure,
  Camera,
  ChartBar,
  FolderOpen,
  Package,
  Siren,
  ClipboardText
} from '@phosphor-icons/react';
import NotificationBell from '@/components/NotificationBell';
import PWAHomeCard from '@/components/PWAHomeCard';
import PushEnrollmentModal from '@/components/PushEnrollmentModal';
import ThemeToggle from '@/components/ThemeToggle';
import { toast } from 'sonner';
import MyTodayCard from '@/components/MyTodayCard';
import { useTheme } from '@/contexts/ThemeContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import SignaturePad from '@/components/SignaturePad';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

/**
 * Map each saturated accent colour used by the dashboard cards to a
 * carefully chosen pastel that (a) clearly contrasts the page's pale
 * lavender background (#F2F0FA), (b) is visibly distinct from its
 * neighbours, and (c) keeps the calm aesthetic the user requested.
 *
 * Saturation lifted to ~25% so the cards "pop" without being loud.
 * A matching subtle border (`borderTint`) anchors each tile against
 * the very-pale page bg.
 */
const PASTEL_MAP = {
  'bg-[#655dc6]': { pastelBg: 'bg-[#DCD8F7]', accentText: 'text-[#4A40A5]', borderTint: 'border-[#B5ACE8]' }, // lavender
  'bg-[#2563EB]': { pastelBg: 'bg-[#CFE0FE]', accentText: 'text-[#1D4ED8]', borderTint: 'border-[#A5C2FA]' }, // royal blue
  'bg-[#059669]': { pastelBg: 'bg-[#BCEBD8]', accentText: 'text-[#047857]', borderTint: 'border-[#86D7B5]' }, // emerald
  'bg-[#D97706]': { pastelBg: 'bg-[#FBDFB4]', accentText: 'text-[#92400E]', borderTint: 'border-[#F2BD7B]' }, // amber
  'bg-[#B91C1C]': { pastelBg: 'bg-[#FBC8C8]', accentText: 'text-[#991B1B]', borderTint: 'border-[#F19898]' }, // rose
  'bg-[#0F766E]': { pastelBg: 'bg-[#B0E0D9]', accentText: 'text-[#0F766E]', borderTint: 'border-[#7AC9BE]' }, // teal
  'bg-[#0891B2]': { pastelBg: 'bg-[#B6E1F0]', accentText: 'text-[#0E7490]', borderTint: 'border-[#7DCBE3]' }, // cyan
  'bg-[#CA8A04]': { pastelBg: 'bg-[#F7E3A6]', accentText: 'text-[#854D0E]', borderTint: 'border-[#E5C66B]' }, // gold
  'bg-[#475569]': { pastelBg: 'bg-[#CBD5E1]', accentText: 'text-[#1F2937]', borderTint: 'border-[#94A3B8]' }, // slate
  'bg-[#7F1D1D]': { pastelBg: 'bg-[#F5B7B7]', accentText: 'text-[#7F1D1D]', borderTint: 'border-[#E78E8E]' }, // dark rose
  'bg-[#D4A843]': { pastelBg: 'bg-[#F5DC9C]', accentText: 'text-[#854D0E]', borderTint: 'border-[#DEBD63]' }, // sand
  'bg-[#4F46E5]': { pastelBg: 'bg-[#CCC8F9]', accentText: 'text-[#3730A3]', borderTint: 'border-[#A29BF1]' }, // indigo
  'bg-[#BE185D]': { pastelBg: 'bg-[#F8C5DC]', accentText: 'text-[#9F1239]', borderTint: 'border-[#EE94BD]' }, // pink
  'bg-[#92400E]': { pastelBg: 'bg-[#F5D4B0]', accentText: 'text-[#7C2D12]', borderTint: 'border-[#E0AE7B]' }, // brown
  'bg-[#7C3AED]': { pastelBg: 'bg-[#DDCBFA]', accentText: 'text-[#5B21B6]', borderTint: 'border-[#BB9CF1]' }, // violet
};

const withPastel = (item) => {
  const p = PASTEL_MAP[item.bgColor] || {
    pastelBg: 'bg-slate-100',
    accentText: 'text-slate-700',
    borderTint: 'border-slate-300',
  };
  return { ...item, ...p };
};

export default function MainDashboard() {
  const { t } = useTranslation();
  const { user, setUser, logout, isAdmin } = useAuth();
  const { language, toggleLanguage, isRTL, direction } = useLanguage();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [deptPendingCount, setDeptPendingCount] = useState(0);
  const [myInvestigationsCount, setMyInvestigationsCount] = useState(0);
  const [myPendingInvestigations, setMyPendingInvestigations] = useState(0);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const heroFileInputRef = React.useRef(null);

  // Custody: items currently held by the user AND items awaiting their signature.
  const [myCustody, setMyCustody] = useState([]);
  const [pendingCustody, setPendingCustody] = useState([]);
  // v1.6.33 — Pending survey nudge on the home hero. We only track the
  // COUNT (not full definitions) so re-renders stay cheap. Clicking the
  // banner routes to `/surveys` which is the full list page.
  const [pendingSurveys, setPendingSurveys] = useState(0);
  // Inline signature modal — opened directly from this card so the
  // employee never needs to leave the home page.
  const [signTarget, setSignTarget] = useState(null);
  const [signName, setSignName] = useState('');
  const [signEmpty, setSignEmpty] = useState(true);
  const [savingSign, setSavingSign] = useState(false);
  const signPadRef = React.useRef(null);

  const handleHeroPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(language === 'ar' ? 'الملف يجب أن يكون صورة' : 'File must be an image');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error(language === 'ar' ? 'حجم الصورة يجب ألا يتجاوز 8 ميغابايت' : 'Image must be under 8 MB');
      return;
    }
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/me/photo`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUser((u) => ({ ...u, photo_path: data.photo_path, photo_updated_at: data.photo_updated_at }));
      toast.success(language === 'ar' ? 'تم تحديث الصورة' : 'Photo updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل رفع الصورة' : 'Upload failed'));
    } finally {
      setUploadingPhoto(false);
      if (heroFileInputRef.current) heroFileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    fetchStats();
    fetchDeptPending();
    fetchMyInvestigations();
    fetchMyCustody();
    fetchPendingSurveys();
  }, []);

  const fetchPendingSurveys = async () => {
    try {
      const { data } = await axios.get(`${API}/my/surveys`, { withCredentials: true });
      const pending = (data || []).filter((s) => !s.already_responded).length;
      setPendingSurveys(pending);
    } catch {
      // Silent — not every user has surveys, and the banner is optional.
    }
  };

  const fetchMyCustody = async () => {
    try {
      const [mine, pending] = await Promise.all([
        axios.get(`${API}/custody/my`, { withCredentials: true }),
        axios.get(`${API}/custody/my-pending`, { withCredentials: true }),
      ]);
      setMyCustody(Array.isArray(mine.data) ? mine.data : []);
      setPendingCustody(Array.isArray(pending.data) ? pending.data : []);
    } catch (err) {
      // silent — custody is non-blocking
    }
  };

  const openSignDialog = (record) => {
    setSignTarget(record);
    setSignName(user?.name || '');
    setSignEmpty(true);
    setTimeout(() => signPadRef.current?.clear(), 50);
  };

  const submitInlineSignature = async () => {
    if (!signTarget) return;
    if (signEmpty) {
      toast.error(language === 'ar' ? 'يرجى رسم توقيعك في المربع المخصص' : 'Please draw your signature');
      return;
    }
    const sig = signPadRef.current?.toDataURL();
    if (!sig) {
      toast.error(language === 'ar' ? 'لم يتم التقاط التوقيع' : 'Signature was not captured');
      return;
    }
    setSavingSign(true);
    try {
      await axios.post(`${API}/custody/${signTarget.id}/employee-sign`, {
        signature_image: sig,
        signature_name: signName || user?.name || '',
      }, { withCredentials: true });
      toast.success(language === 'ar' ? 'تم توقيع العهدة' : 'Custody signed');
      setSignTarget(null);
      await fetchMyCustody();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'فشل توقيع العهدة' : 'Signing failed'));
    } finally {
      setSavingSign(false);
    }
  };

  const fetchMyInvestigations = async () => {
    try {
      const { data } = await axios.get(`${API}/investigations/mine`, { withCredentials: true });
      setMyInvestigationsCount(data.length);
      setMyPendingInvestigations(data.filter(x => !x.my_statement_submitted).length);
    } catch {
      // Not applicable - ignore
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API}/dashboard/stats`, { withCredentials: true });
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchDeptPending = async () => {
    try {
      const { data } = await axios.get(`${API}/leaves/department-pending`, { withCredentials: true });
      setDeptPendingCount(data.length);
    } catch {
      // Not a department manager - ignore
    }
  };

  const handleLogout = async () => {
    const target = isAdmin ? '/' : '/login';
    navigate(target);
    await logout();
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Admin menu items with different colors
  const adminMenuItems = [
    { 
      path: '/admin/dashboard', 
      icon: House, 
      label: 'dashboard',
      bgColor: 'bg-[#655dc6]',
      hoverColor: 'hover:bg-[#544cb0]',
      description_ar: 'لوحة التحكم الرئيسية',
      description_en: 'Main Control Panel'
    },
    { 
      path: '/admin/employees', 
      icon: Users, 
      label: 'employees',
      bgColor: 'bg-[#2563EB]',
      hoverColor: 'hover:bg-[#1D4ED8]',
      description_ar: 'إدارة بيانات الموظفين',
      description_en: 'Manage Employee Data'
    },
    { 
      path: '/admin/attendance', 
      icon: Clock, 
      label: 'attendance',
      bgColor: 'bg-[#059669]',
      hoverColor: 'hover:bg-[#047857]',
      description_ar: 'الحضور · تصحيحات البصمة · التدقيق الأمني',
      description_en: 'Attendance · Corrections · Security Audit'
    },
    { 
      path: '/admin/leaves', 
      icon: CalendarCheck, 
      label: 'leaves',
      bgColor: 'bg-[#D97706]',
      hoverColor: 'hover:bg-[#B45309]',
      description_ar: 'الإجازات · الأرصدة · المخالصات',
      description_en: 'Leaves · Balances · Clearances'
    },
    { 
      path: '/admin/payroll', 
      icon: Money, 
      label: 'payroll',
      bgColor: 'bg-[#B91C1C]',
      hoverColor: 'hover:bg-[#991B1B]',
      description_ar: 'كشوف الرواتب الشهرية',
      description_en: 'Monthly Payroll'
    },
    { 
      path: '/admin/qiwa', 
      icon: Buildings, 
      label: 'qiwa_management',
      bgColor: 'bg-[#0F766E]',
      hoverColor: 'hover:bg-[#115E59]',
      description_ar: 'متابعة قوى والتأمينات الاجتماعية للموظفين',
      description_en: 'Qiwa & GOSI Employee Status'
    },
    {
      path: '/admin/requests', 
      icon: FileText, 
      label: 'requests_management',
      bgColor: 'bg-[#0891B2]',
      hoverColor: 'hover:bg-[#0E7490]',
      description_ar: 'طلبات الخطابات وتعريف الراتب',
      description_en: 'Letter Requests & Certificates'
    },
    { 
      path: '/admin/notifications', 
      icon: Bell, 
      label: 'notifications',
      bgColor: 'bg-[#92400E]',
      hoverColor: 'hover:bg-[#7C2D12]',
      description_ar: 'إشعارات داخلية للموظفين',
      description_en: 'In-App Notifications'
    },
    {
      path: '/recruitment',
      icon: Briefcase,
      label: 'recruitment_portal',
      bgColor: 'bg-[#BE185D]',
      hoverColor: 'hover:bg-[#9F1239]',
      description_ar: 'إدارة الشواغر · السير الذاتية · المرشحون',
      description_en: 'Vacancies · CVs · Candidates'
    },
    {
      path: '/admin/government',
      icon: Bank,
      label: 'government_relations',
      bgColor: 'bg-[#4F46E5]',
      hoverColor: 'hover:bg-[#4338CA]',
      description_ar: 'تراخيص المنشأة · الإقامات · الجوازات',
      description_en: 'Facility · Residencies · Passports'
    },
    { 
      path: '/admin/org-chart', 
      icon: TreeStructure, 
      label: 'org_chart',
      bgColor: 'bg-[#7C3AED]',
      hoverColor: 'hover:bg-[#6D28D9]',
      description_ar: 'مخطط الهيكل التنظيمي للشركة',
      description_en: 'Visual organisation chart'
    },
    { 
      path: '/admin/investigations', 
      icon: Scales, 
      label: 'investigations',
      bgColor: 'bg-[#EA580C]',
      hoverColor: 'hover:bg-[#C2410C]',
      description_ar: 'التحقيقات الداخلية مع الموظفين',
      description_en: 'Internal Employee Investigations'
    },
    {
      path: '/admin/custody',
      icon: Package,
      label: 'custody',
      bgColor: 'bg-[#7C3AED]',
      hoverColor: 'hover:bg-[#6D28D9]',
      description_ar: 'عهد الموظفين — الأجهزة والبطاقات والأختام',
      description_en: 'Employee custody — devices, cards, stamps'
    },
    {
      path: '/admin/incidents',
      icon: Siren,
      label: 'incidents',
      bgColor: 'bg-[#E11D48]',
      hoverColor: 'hover:bg-[#BE123C]',
      description_ar: 'بلاغات الحوادث والشكاوى — تحليل وتحويل ومتابعة',
      description_en: 'Incident reports — analyse, forward and track'
    },
    {
      path: '/admin/surveys',
      icon: ClipboardText,
      label: 'surveys',
      bgColor: 'bg-[#0891B2]',
      hoverColor: 'hover:bg-[#0E7490]',
      description_ar: 'استبيانات إدارة الجودة — إرسال فوري ونتائج لحظية',
      description_en: 'Quality surveys — instant delivery and live results'
    },
    {
      path: '/admin/reports',
      icon: ChartBar,
      label: 'reports',
      bgColor: 'bg-[#0F766E]',
      hoverColor: 'hover:bg-[#134E4A]',
      description_ar: 'تقارير الحضور والرواتب وتقرير موظف مفصّل',
      description_en: 'Attendance · Payroll · Employee-specific reports'
    },
    { 
      path: '/admin/settings', 
      icon: GearSix, 
      label: 'settings',
      bgColor: 'bg-[#475569]',
      hoverColor: 'hover:bg-[#334155]',
      description_ar: 'إعدادات النظام والعطل الرسمية',
      description_en: 'System Settings & Holidays'
    },
  ];

  // Employee menu items
  const employeeMenuItems = [
    { 
      path: '/employee/dashboard', 
      icon: House, 
      label: 'dashboard',
      bgColor: 'bg-[#655dc6]',
      hoverColor: 'hover:bg-[#544cb0]',
      description_ar: 'لوحة التحكم الشخصية',
      description_en: 'Personal Dashboard'
    },
    { 
      path: '/employee/attendance', 
      icon: ClockClockwise, 
      label: 'my_attendance',
      bgColor: 'bg-[#059669]',
      hoverColor: 'hover:bg-[#047857]',
      description_ar: 'سجل الحضور والانصراف',
      description_en: 'Attendance Records'
    },
    { 
      path: '/employee/leaves', 
      icon: CalendarCheck, 
      label: 'my_leaves',
      bgColor: 'bg-[#D97706]',
      hoverColor: 'hover:bg-[#B45309]',
      description_ar: 'طلب وإدارة الإجازات',
      description_en: 'Request & Manage Leaves'
    },
    { 
      path: '/employee/payslips', 
      icon: FileText, 
      label: 'my_payslips',
      bgColor: 'bg-[#2563EB]',
      hoverColor: 'hover:bg-[#1D4ED8]',
      description_ar: 'كشوف الراتب الشهرية',
      description_en: 'Monthly Payslips'
    },
    { 
      path: '/employee/requests', 
      icon: Receipt, 
      label: 'my_requests',
      bgColor: 'bg-[#0891B2]',
      hoverColor: 'hover:bg-[#0E7490]',
      description_ar: 'تعريف راتب · شهادة خبرة · لا مانع',
      description_en: 'Salary cert · Experience · NOC'
    },
    {
      path: '/employee/attendance-corrections',
      icon: ClockClockwise,
      label: 'attendance_corrections',
      bgColor: 'bg-[#4F46E5]',
      hoverColor: 'hover:bg-[#4338CA]',
      description_ar: 'طلب تصحيح بصمة (نسيان أو ظرف طارئ)',
      description_en: 'Attendance correction (forgot / emergency)'
    },
    {
      path: '/employee/my-documents',
      icon: FolderOpen,
      label: 'my_documents',
      bgColor: 'bg-[#B45309]',
      hoverColor: 'hover:bg-[#92400E]',
      description_ar: 'شهادات التدريب والمستندات الرسمية',
      description_en: 'Training certificates & HR documents'
    },
    {
      path: '/employee/incidents',
      icon: Siren,
      label: 'incidents',
      bgColor: 'bg-[#E11D48]',
      hoverColor: 'hover:bg-[#BE123C]',
      description_ar: 'تقديم بلاغ حادث أو شكوى ومتابعة حالته',
      description_en: 'Submit an incident / complaint and track it'
    },
    {
      path: '/surveys',
      icon: ClipboardText,
      label: 'my_surveys',
      bgColor: 'bg-[#0891B2]',
      hoverColor: 'hover:bg-[#0E7490]',
      description_ar: 'استبيانات إدارة الجودة المرسلة لك — 2-3 دقائق فقط',
      description_en: 'Quality surveys sent to you — just 2-3 minutes'
    },
  ];

  const isDeptManager = user?.is_department_manager;
  const isFinanceMgr = !!user?.is_finance_manager;
  const isGM = user?.role === 'general_manager' || !!user?.is_general_manager;
  const isITMgr = !!user?.is_it_manager;

  let menuItems = [];
  if (isAdmin) {
    menuItems = adminMenuItems;
  } else if (isGM) {
    // General Manager sees their approval-flow cards AS WELL AS the personal
    // employee cards — the GM is also an employee who takes leaves, has
    // attendance and requests. Approval cards come first (primary role),
    // followed by a divider label + personal cards.
    const gmApprovalCards = [
      ...adminMenuItems.filter(i => ['/admin/dashboard'].includes(i.path)),
      {
        path: '/admin/gm-recruitment',
        icon: Briefcase,
        label: 'gm_recruitment',
        bgColor: 'bg-[#D4A843]',
        hoverColor: 'hover:bg-[#B8912E]',
        description_ar: 'اعتمادات التوظيف',
        description_en: 'Recruitment Approvals'
      },
      {
        path: '/admin/leaves',
        icon: CalendarCheck,
        label: 'leaves',
        bgColor: 'bg-[#D97706]',
        hoverColor: 'hover:bg-[#B45309]',
        description_ar: 'اعتمادات الإجازات',
        description_en: 'Leave Approvals'
      },
      {
        path: '/admin/clearance',
        icon: Receipt,
        label: 'clearance',
        bgColor: 'bg-[#10B981]',
        hoverColor: 'hover:bg-[#059669]',
        description_ar: 'مخالصات بدل الإجازة',
        description_en: 'Leave Clearances'
      },
      {
        path: '/admin/attendance',
        icon: Clock,
        label: 'attendance',
        bgColor: 'bg-[#0891B2]',
        hoverColor: 'hover:bg-[#0E7490]',
        description_ar: 'الحضور والغائبون (للاطلاع)',
        description_en: 'Attendance & Absentees (read-only)'
      },
    ];
    // Personal employee cards — hide `/employee/dashboard` since the GM
    // already has a control-panel card at the top of the menu (avoids
    // two identical-looking "Dashboard" cards).
    const gmPersonalCards = employeeMenuItems.filter(i => i.path !== '/employee/dashboard');
    menuItems = [...gmApprovalCards, ...gmPersonalCards];
  } else {
    menuItems = [...employeeMenuItems];

    // ----- Agency Contracted Workers (موظف مورد قوى عاملة) -----
    // Per company policy these workers get attendance + overtime tracking
    // ONLY for company benefits — but they STILL file leaves for
    // visibility/approval (just for the record, no clearance or ticket).
    // Hidden: payslips + service requests (handled by their supplier).
    if (user?.is_contracted_worker) {
      const allowedPaths = new Set([
        '/employee/dashboard',
        '/employee/attendance',
        '/employee/leaves',
        '/employee/attendance-corrections',
      ]);
      menuItems = menuItems.filter(i => allowedPaths.has(i.path));
    }

    // Doctors (any type) never punch attendance — hide both the attendance
    // record card and the corrections card.
    if (user?.is_doctor) {
      menuItems = menuItems.filter(i =>
        i.path !== '/employee/attendance' &&
        i.path !== '/employee/attendance-corrections'
      );
    }

    // v1.5.42 — Scoped Government Affairs delegation. When a regular
    // employee is flagged `is_government_officer`, expose the same
    // Government tile the admins see (data entry + upload only — the
    // backend denies deletes). All personal employee tiles stay
    // visible so this employee's clock-in / leaves / payslips work
    // normally.
    if (user?.is_government_officer) {
      const govtCard = adminMenuItems.find(i => i.path === '/admin/government');
      if (govtCard) menuItems.push(govtCard);
    }

    if (isDeptManager) {
      // Resolve dept name in the active language. Falls back to the raw
      // value if the dept exists only as a sub-dept (then look it up there).
      let deptName = getDeptLabel(user?.department, language);
      if (deptName === user?.department && user?.department) {
        for (const top of DEPARTMENTS) {
          const sub = (top.subs || []).find(s => s.value === user.department);
          if (sub) { deptName = language === 'en' ? sub.label_en : sub.label_ar; break; }
        }
      }
      const deptLabel = user?.department
        ? (language === 'ar' ? `قسم: ${deptName}` : `Dept: ${deptName}`)
        : (language === 'ar' ? 'قسمك' : 'Your Department');
      menuItems.push({
        path: '/admin/dept-recruitment',
        icon: Briefcase,
        label: 'dept_recruitment',
        bgColor: 'bg-[#D4A843]',
        hoverColor: 'hover:bg-[#B8912E]',
        description_ar: deptLabel,
        description_en: deptLabel
      });
      // Department managers are the FIRST approver in the leave chain for
      // their team members — expose a dedicated home-page card so they
      // don't have to navigate through the admin menu to find requests.
      menuItems.push({
        path: '/admin/leaves',
        icon: CalendarCheck,
        label: 'dept_leave_approvals',
        bgColor: 'bg-[#D97706]',
        hoverColor: 'hover:bg-[#B45309]',
        description_ar: language === 'ar' ? `اعتمادات إجازات ${deptLabel}` : `Leave approvals — ${deptLabel}`,
        description_en: language === 'ar' ? `اعتمادات إجازات ${deptLabel}` : `Leave approvals — ${deptLabel}`,
      });
      // NOTE: Attendance-correction approvals are admin-only by policy.
      // The card is intentionally NOT added for department managers.
    }
    if (isFinanceMgr) {
      // Single unified card — same as the admin's `leaves` card — because
      // `/admin/leaves` already exposes the Clearances tab. Keeping a
      // separate Finance Clearances card duplicated navigation and
      // contradicted the "tabs inside Leaves" UX. Description names both
      // sub-sections so finance managers immediately know where to click.
      menuItems.push({
        path: '/admin/leaves',
        icon: CalendarCheck,
        label: 'leaves',
        bgColor: 'bg-[#D97706]',
        hoverColor: 'hover:bg-[#B45309]',
        description_ar: 'الإجازات · الأرصدة · المخالصات للاعتماد المالي',
        description_en: 'Leaves · Balances · Finance Clearances'
      });
      // Finance manager needs read-only view on attendance (to verify hours
      // before signing off payroll) and access to the payroll module
      // (overtime + payslips with finance-approval permissions).
      menuItems.push({
        path: '/admin/attendance',
        icon: Clock,
        label: 'attendance',
        bgColor: 'bg-[#0891B2]',
        hoverColor: 'hover:bg-[#0E7490]',
        description_ar: 'الحضور والإنصراف (للاعتماد المالي)',
        description_en: 'Attendance (Finance Review)'
      });
      menuItems.push({
        path: '/admin/payroll',
        icon: Money,
        label: 'payroll',
        bgColor: 'bg-[#7C3AED]',
        hoverColor: 'hover:bg-[#6D28D9]',
        description_ar: 'الإضافي وكشف الرواتب',
        description_en: 'Overtime & Payslips'
      });
      // v1.6.6 — Finance manager now has access to the Reports page
      // (Attendance / Payroll / Employee Report). Needed to pull a
      // detailed per-employee attendance + leaves + OT breakdown to
      // reconcile payslip amounts before publishing payroll.
      menuItems.push({
        path: '/admin/reports',
        icon: ChartBar,
        label: 'reports',
        bgColor: 'bg-[#0F766E]',
        hoverColor: 'hover:bg-[#134E4A]',
        description_ar: 'تقارير الحضور والرواتب وتقرير موظف مفصّل',
        description_en: 'Attendance · Payroll · Employee-specific reports'
      });
    }
    if (isITMgr) {
      // IT manager sees only forwarded id_card requests via the shared
      // /admin/requests page (backend force-filters the queue for them).
      menuItems.push({
        path: '/admin/requests',
        icon: FileText,
        label: 'dept_requests',
        bgColor: 'bg-[#4F46E5]',
        hoverColor: 'hover:bg-[#4338CA]',
        description_ar: 'طلبات بطاقات التعريف — قسم تقنية المعلومات',
        description_en: 'ID-card requests — IT Department'
      });
    }
  }

  // "My Investigations" card — shows for regular employees always, but for
  // admins only when they actually have an investigation record against them.
  {
    const hasPending = myPendingInvestigations > 0;
    const hasAny = myInvestigationsCount > 0;
    const shouldShow = !isAdmin || hasAny;   // hide from admins unless they're a party
    if (shouldShow) {
      menuItems.push({
        path: '/employee/investigations',
        icon: Scales,
        label: 'my_investigations',
        bgColor: hasPending ? 'bg-[#DC2626]' : 'bg-[#655dc6]',
        hoverColor: hasPending ? 'hover:bg-[#B91C1C]' : 'hover:bg-[#544cb0]',
        description_ar: hasPending
          ? `مطلوب منك تدوين أقوالك (${myPendingInvestigations})`
          : (hasAny ? `تحقيقاتي (${myInvestigationsCount})` : 'تحقيقاتي'),
        description_en: hasPending
          ? `Statement required (${myPendingInvestigations})`
          : (hasAny ? `My Investigations (${myInvestigationsCount})` : 'My Investigations'),
      });
    }
  }

  return (
    <div
      className="min-h-screen relative"
      style={{
        background: isDark
          ? 'linear-gradient(135deg, #14121f 0%, #17142a 50%, #1a1630 100%)'
          : 'linear-gradient(135deg, #F5F3FA 0%, #ECE9F7 50%, #E5E1F4 100%)',
      }}
      data-testid="main-dashboard"
      dir={direction}
    >
      {/* Soft brand halos — same as login */}
      <div
        aria-hidden
        className="fixed top-[-15%] start-[-10%] w-[500px] h-[500px] rounded-full blur-[120px] pointer-events-none"
        style={{ background: 'rgba(101, 93, 198, 0.12)' }}
      />
      <div
        aria-hidden
        className="fixed bottom-[-15%] end-[-10%] w-[500px] h-[500px] rounded-full blur-[120px] pointer-events-none"
        style={{ background: 'rgba(212, 168, 67, 0.08)' }}
      />
      {/* Header */}
      <header className="z-40 bg-white/95 backdrop-blur-xl border-b border-white/60 shadow-[0_1px_8px_rgba(101,93,198,0.06)] sticky top-0">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src={LOGO_URL} 
                alt="Lucent Logo" 
                className="h-12 w-auto object-contain"
              />
            </div>

            {/* User Menu */}
            <div className="flex items-center gap-2">
              <NotificationBell />
              <ThemeToggle />
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                className="text-[#655dc6] hover:text-[#544cb0] hover:bg-[#E8E6F5]"
                data-testid="header-language-toggle"
              >
                <Globe size={20} className="me-2" />
                {language === 'ar' ? 'EN' : 'عربي'}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center gap-3 hover:bg-[#E8E6F5]"
                    data-testid="user-menu-trigger"
                  >
                    <EmployeeAvatar
                      userId={user?.id}
                      name={language === 'en' && user?.name_en ? user.name_en : user?.name}
                      photoPath={user?.photo_path}
                      photoUpdatedAt={user?.photo_updated_at}
                      size={36}
                    />
                    <div className="text-start hidden sm:block">
                      <p className="text-sm font-medium text-[#655dc6]">{language === 'en' && user?.name_en ? user.name_en : user?.name}</p>
                      <p className="text-xs text-[#655dc6]/60">
                        {user?.role === 'admin' ? t('system_admin') : (language === 'en' ? (user?.position_en || user?.position) : user?.position)}
                      </p>
                    </div>
                    <CaretDown size={16} className="text-[#655dc6]/60" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align={isRTL ? 'start' : 'end'} className="w-48">
                  <DropdownMenuItem onClick={toggleLanguage} data-testid="menu-language">
                    <Globe size={18} className="me-2" />
                    {language === 'ar' ? 'English' : 'العربية'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {!isAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/employee/profile')} className="text-[#655dc6]" data-testid="menu-profile">
                      <UserCircle size={18} className="me-2" />
                      {t('my_profile')}
                    </DropdownMenuItem>
                  )}
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/admin/profile')} className="text-[#655dc6]" data-testid="menu-admin-profile">
                      <UserCircle size={18} className="me-2" />
                      {t('my_profile')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                    <SignOut size={18} className="me-2" />
                    {t('logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col" style={{ minHeight: 'calc(100vh - 64px)' }}>
        {/* v1.6.33 — Pending survey nudge. Bright, dismissible-looking
            banner that funnels employees to their survey queue without
            an extra email. Shown ONLY when there is at least one
            pending (not-responded) survey from the Quality Dept. */}
        {pendingSurveys > 0 && (
          <button
            type="button"
            onClick={() => navigate('/surveys')}
            data-testid="pending-surveys-banner"
            className="group mb-3 w-full relative overflow-hidden rounded-2xl text-start transition-transform hover:scale-[1.005] active:scale-[0.995] focus:outline-none focus:ring-4 focus:ring-sky-400/40"
            style={{
              background: 'linear-gradient(135deg, #0891B2 0%, #0E7490 55%, #164E63 100%)',
              boxShadow: '0 10px 30px -8px rgba(8, 145, 178, 0.55)',
            }}
          >
            {/* Decorative floating circles — same vibe as the hero */}
            <span aria-hidden className="pointer-events-none absolute -top-10 -end-10 h-32 w-32 rounded-full bg-white/15 blur-sm" />
            <span aria-hidden className="pointer-events-none absolute -bottom-6 -start-6 h-24 w-24 rounded-full bg-white/10 blur-md" />

            <div className="relative flex items-center gap-4 p-4 sm:p-5">
              {/* Pulsing clipboard icon */}
              <div className="relative shrink-0 h-14 w-14 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center ring-2 ring-white/40">
                <ClipboardText size={28} weight="fill" className="text-white" />
                <span className="absolute -top-1 -end-1 min-w-[22px] h-[22px] px-1.5 rounded-full bg-amber-400 text-amber-900 text-xs font-bold flex items-center justify-center ring-2 ring-white shadow">
                  {pendingSurveys}
                </span>
                <span className="absolute inset-0 rounded-2xl ring-2 ring-white/30 animate-ping" />
              </div>

              <div className="flex-1 min-w-0 text-white">
                <div className="font-bold text-base sm:text-lg leading-tight">
                  {language === 'ar'
                    ? (pendingSurveys === 1
                        ? 'لديك استبيان من إدارة الجودة بانتظارك'
                        : `لديك ${pendingSurveys} استبيانات من إدارة الجودة بانتظارك`)
                    : `You have ${pendingSurveys} pending ${pendingSurveys === 1 ? 'survey' : 'surveys'} from Quality`}
                </div>
                <div className="text-xs sm:text-sm text-white/85 mt-0.5">
                  {language === 'ar'
                    ? '2-3 دقائق فقط · إجاباتك سرية وتُسهم في تطوير بيئة العمل'
                    : 'Just 2-3 minutes · Your answers are confidential and shape the workplace'}
                </div>
              </div>

              <div className="shrink-0 flex items-center gap-1 text-white text-sm font-semibold px-3 py-1.5 rounded-full bg-white/15 group-hover:bg-white/25 transition-colors">
                {language === 'ar' ? 'ابدأ الآن' : 'Start Now'}
                <span aria-hidden className="rtl:rotate-180">→</span>
              </div>
            </div>
          </button>
        )}

        {/* PWA install + push-notification prompt — inline, always visible
            until both actions are completed. */}
        <PWAHomeCard />

        {/* Auto-popup nudge: opens once every 7 days for users who haven't
            granted push permission yet. Skips silently if granted/denied. */}
        <PushEnrollmentModal />

        {/* Welcome Section — professional hero card with employee photo */}
        <div
          className="mb-3 relative overflow-hidden rounded-2xl border border-[#C4BFF0] bg-gradient-to-l from-white via-[#F2F0FA] to-[#E8E6F5] shadow-sm"
          data-testid="welcome-hero"
        >
          {/* Decorative purple ring in the top-end corner */}
          <div className="pointer-events-none absolute -top-14 -end-14 h-40 w-40 rounded-full bg-[#655dc6]/10 blur-sm" />
          <div className="pointer-events-none absolute -bottom-10 -start-10 h-32 w-32 rounded-full bg-[#C4BFF0]/40 blur-md" />
          <div className="relative flex items-center gap-4 sm:gap-5 p-4 sm:p-5">
            {/* Click-to-change avatar — hovering reveals a subtle camera overlay */}
            <button
              type="button"
              onClick={() => heroFileInputRef.current?.click()}
              disabled={uploadingPhoto}
              title={language === 'ar' ? 'تغيير الصورة' : 'Change photo'}
              aria-label={language === 'ar' ? 'تغيير الصورة الشخصية' : 'Change profile photo'}
              className="relative shrink-0 rounded-full focus:outline-none focus:ring-4 focus:ring-[#655dc6]/30 group"
              data-testid="hero-avatar-upload"
            >
              <EmployeeAvatar
                userId={user?.id}
                name={language === 'en' && user?.name_en ? user.name_en : user?.name}
                photoPath={user?.photo_path}
                photoUpdatedAt={user?.photo_updated_at}
                size={72}
                fontClass="text-2xl"
                className="ring-4 ring-white shadow-md transition-transform group-hover:scale-[1.03]"
              />
              {/* Hover overlay — camera icon appears on mouse over */}
              <span className="pointer-events-none absolute inset-0 rounded-full bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                {uploadingPhoto ? (
                  <span className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Camera size={22} weight="fill" className="text-white" />
                )}
              </span>
              <input
                ref={heroFileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleHeroPhotoUpload}
                data-testid="hero-photo-input"
              />
            </button>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wider text-[#655dc6]/60 font-semibold mb-0.5">
                {t('welcome_back')}
              </p>
              <h1 className="text-xl sm:text-2xl font-bold text-[#655dc6] break-words leading-tight">
                {language === 'en' && user?.name_en ? user.name_en : user?.name}
              </h1>
              <p className="text-[#655dc6]/70 mt-1 text-xs sm:text-sm">
                {isAdmin ? t('admin_subtitle') : t('employee_subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* "My Today" panel — plain employees & managers (not admin) */}
        {!isAdmin && <MyTodayCard />}

        {/* Quick Stats */}
        {(isAdmin || isFinanceMgr) && stats && (
          <div className={`grid grid-cols-2 ${isFinanceMgr ? 'sm:grid-cols-3 lg:grid-cols-5' : 'sm:grid-cols-4'} gap-2 sm:gap-3 mb-3`} data-testid="quick-stats">
            <div className="bg-white rounded-xl px-3 py-2.5 sm:px-4 flex items-center gap-2 sm:gap-3 border border-[#C4BFF0]/50 shadow-sm min-w-0">
              <div className="w-9 h-9 rounded-lg bg-[#655dc6]/10 flex items-center justify-center flex-shrink-0">
                <Users size={20} weight="duotone" className="text-[#655dc6]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-xs text-[#655dc6]/60 truncate">{t('total_employees')}</p>
                <p className="text-base sm:text-lg font-bold text-[#655dc6]" data-testid="stat-total-employees">{stats.total_employees || 0}</p>
              </div>
            </div>
            <div className="bg-white rounded-xl px-3 py-2.5 sm:px-4 flex items-center gap-2 sm:gap-3 border border-[#059669]/20 shadow-sm min-w-0">
              <div className="w-9 h-9 rounded-lg bg-[#059669]/10 flex items-center justify-center flex-shrink-0">
                <Clock size={20} weight="duotone" className="text-[#059669]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-xs text-[#059669]/60 truncate">{t('present_today')}</p>
                <p className="text-base sm:text-lg font-bold text-[#059669]" data-testid="stat-present-today">{stats.present_today || 0}</p>
              </div>
            </div>
            <div className="bg-white rounded-xl px-3 py-2.5 sm:px-4 flex items-center gap-2 sm:gap-3 border border-[#DC2626]/20 shadow-sm min-w-0">
              <div className="w-9 h-9 rounded-lg bg-[#DC2626]/10 flex items-center justify-center flex-shrink-0">
                <CalendarX size={20} weight="duotone" className="text-[#DC2626]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-xs text-[#DC2626]/60 truncate">{t('absent_today')}</p>
                <p className="text-base sm:text-lg font-bold text-[#DC2626]" data-testid="stat-absent-today">{stats.absent_today || 0}</p>
              </div>
            </div>
            {isFinanceMgr && (
              <div className="bg-white rounded-xl px-3 py-2.5 sm:px-4 flex items-center gap-2 sm:gap-3 border border-[#10B981]/20 shadow-sm min-w-0">
                <div className="w-9 h-9 rounded-lg bg-[#10B981]/10 flex items-center justify-center flex-shrink-0">
                  <Receipt size={20} weight="duotone" className="text-[#10B981]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] sm:text-xs text-[#10B981]/70 truncate">
                    {language === 'ar' ? 'مخالصات للاعتماد' : 'Pending Clearances'}
                  </p>
                  <p className="text-base sm:text-lg font-bold text-[#10B981]" data-testid="stat-pending-finance-clearances">
                    {stats.pending_finance_clearances || 0}
                  </p>
                </div>
              </div>
            )}
            {/* 4th tile: admin sees "غير خاضعين للبصمة" (since admin
                doesn't sit in the leave approval chain in the normal
                flow — that card was noise). Other roles keep the
                "pending leaves" tile here. */}
            {isAdmin ? (
              <div className="bg-white rounded-xl px-3 py-2.5 sm:px-4 flex items-center gap-2 sm:gap-3 border border-[#92400E]/20 shadow-sm min-w-0">
                <div className="w-9 h-9 rounded-lg bg-[#92400E]/10 flex items-center justify-center flex-shrink-0">
                  <Users size={20} weight="duotone" className="text-[#92400E]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] sm:text-xs text-[#92400E]/70 truncate">{t('off_site_employees')}</p>
                  <p className="text-base sm:text-lg font-bold text-[#92400E]" data-testid="stat-off-site-employees">{stats.off_site_employees || 0}</p>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl px-3 py-2.5 sm:px-4 flex items-center gap-2 sm:gap-3 border border-[#D97706]/20 shadow-sm min-w-0">
                <div className="w-9 h-9 rounded-lg bg-[#D97706]/10 flex items-center justify-center flex-shrink-0">
                  <CalendarCheck size={20} weight="duotone" className="text-[#D97706]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] sm:text-xs text-[#D97706]/60 truncate">{t('pending_leaves')}</p>
                  <p className="text-base sm:text-lg font-bold text-[#D97706]" data-testid="stat-pending-leaves">{stats.pending_leaves || 0}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ─── عُهدي — custody held + awaiting signature ───────────────
            Rendered BEFORE the big tile grid so the employee notices
            any pending signature the moment they land on the home page
            (no need to open the dashboard). The amber banner reveals a
            sign-now button that opens a signature-pad modal right here. */}
        {(pendingCustody.length > 0 || myCustody.length > 0) && (
          <div className="mb-6 space-y-4" data-testid="home-custody-section">
            {pendingCustody.length > 0 && (
              <div className="rounded-2xl border border-amber-300 bg-amber-50 dark:bg-amber-900/15 dark:border-amber-700/60 p-4 shadow-sm" data-testid="home-pending-custody">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-amber-500 text-white font-bold">!</span>
                  <h3 className="text-base font-bold text-amber-800 dark:text-amber-100">
                    {language === 'ar' ? 'عهد بانتظار توقيعك' : 'Custody awaiting your signature'}
                  </h3>
                  <span className="text-[11px] font-semibold text-amber-700 dark:text-amber-300 ms-auto">
                    {pendingCustody.length} {language === 'ar' ? 'سجل' : 'items'}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {pendingCustody.map((r) => (
                    <div key={r.id} className="rounded-lg border border-amber-300 dark:border-amber-700/70 bg-white dark:bg-slate-900 p-3" data-testid={`home-pending-${r.id}`}>
                      <div className="flex items-start gap-2">
                        <Package size={20} weight="duotone" className="text-amber-600 dark:text-amber-300 shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-[#262263] dark:text-amber-100 truncate">{r.asset?.name || '—'}</div>
                          {r.asset?.serial_number && (
                            <div className="text-[11px] font-mono text-gray-500 dark:text-slate-400 mt-0.5">SN: {r.asset.serial_number}</div>
                          )}
                          <div className="text-[11px] text-gray-500 dark:text-slate-400 mt-1">
                            {language === 'ar' ? 'طُلب في' : 'Requested on'}: <span className="font-mono">{(r.handover_at || '').slice(0, 10)}</span>
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => openSignDialog(r)}
                        className="mt-3 w-full h-8 bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid={`home-sign-btn-${r.id}`}
                      >
                        {language === 'ar' ? 'توقيع الآن' : 'Sign now'}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {myCustody.length > 0 && (
              <div className="rounded-2xl border border-[#C4BFF0] dark:border-slate-700 bg-white dark:bg-slate-900 p-4 shadow-sm" data-testid="home-my-custody">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-200">
                    <Package size={20} weight="duotone" />
                  </span>
                  <h3 className="text-base font-bold text-[#655dc6] dark:text-violet-200">
                    {language === 'ar' ? 'عُهدي الحالية' : 'My current custody'}
                  </h3>
                  <span className="text-[11px] font-semibold text-[#655dc6]/70 dark:text-slate-400 ms-auto">
                    {language === 'ar' ? `${myCustody.length} صنف` : `${myCustody.length} items`}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {myCustody.map((r) => (
                    <div key={r.id} className="rounded-lg border border-[#C4BFF0] dark:border-slate-700 bg-[#F8F7FC] dark:bg-slate-800/60 p-3" data-testid={`home-my-${r.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-[#262263] dark:text-slate-100 truncate">{r.asset?.name || '—'}</div>
                          {r.asset?.serial_number && (
                            <div className="text-[11px] font-mono text-gray-500 dark:text-slate-400 mt-0.5">SN: {r.asset.serial_number}</div>
                          )}
                          {r.asset?.brand && (
                            <div className="text-[11px] text-gray-500 dark:text-slate-400">{r.asset.brand} {r.asset.model && `· ${r.asset.model}`}</div>
                          )}
                        </div>
                        <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-200">
                          {language === 'ar' ? 'نشطة' : 'Active'}
                        </span>
                      </div>
                      <div className="text-[11px] text-gray-500 dark:text-slate-400 mt-2 flex items-center justify-between">
                        <span>
                          {language === 'ar' ? 'استلمت' : 'Received'}: <span className="font-mono">{(r.handover_at || '').slice(0, 10)}</span>
                        </span>
                        <a
                          href={`${API}/custody/${r.id}/receipt.pdf?kind=handover`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[#655dc6] dark:text-violet-300 hover:underline"
                          data-testid={`home-receipt-${r.id}`}
                        >
                          {language === 'ar' ? 'عرض السند' : 'View receipt'}
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Grid Menu — bright white cards with accent strip + glow on hover,
            mirroring the login portal cards for cohesion. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="menu-grid">
          {menuItems.map(withPastel).map((item, index) => {
            // Extract the raw accent hex from the "bg-[#xxxxxx]" tailwind utility.
            const accentHex = (item.bgColor || 'bg-[#655dc6]').match(/#[0-9A-Fa-f]{6}/)?.[0] || '#655dc6';
            const glow = `${accentHex}22`;   // 13% alpha for the soft halo
            const tint = `${accentHex}0F`;   // 6% alpha for the hover background fade
            // Base card tint — a soft wash of the accent over white so
            // each card is visually distinguished by colour while staying
            // calm and refined. Start ~12% alpha, fade to ~7% on the diagonal.
            const baseBg = isDark
              ? `linear-gradient(135deg, ${accentHex}2E 0%, ${accentHex}14 100%), #1d1a30`
              : `linear-gradient(135deg, ${accentHex}1F 0%, ${accentHex}12 100%), #ffffff`;
            const badgePos = (language === 'en') ? 'right-3' : 'left-3';
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className="group relative flex flex-col items-start text-start p-5 rounded-2xl border border-[#EDE9FE] shadow-[0_4px_18px_-6px_rgba(101,93,198,0.12)] transition-all duration-500 cursor-pointer overflow-hidden min-h-[160px] hover:-translate-y-1 hover:shadow-[0_18px_36px_-12px_rgba(101,93,198,0.24)] focus:outline-none focus:ring-2 focus:ring-[#655dc6]/30"
                data-testid={`menu-item-${item.label}`}
                style={{
                  background: baseBg,
                  borderTop: `3px solid ${accentHex}`,
                  borderColor: `${accentHex}33`,
                }}
              >
                {/* Soft tint fade on hover */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{ background: `linear-gradient(135deg, ${tint} 0%, transparent 65%)` }}
                />
                {/* Hover halo */}
                <div
                  className="absolute -top-16 -end-16 w-36 h-36 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                  style={{ background: glow }}
                />
                {/* Animated bottom strip */}
                <div
                  className="absolute bottom-0 inset-x-0 h-[2px] origin-start scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
                  style={{ background: accentHex }}
                />

                {/* Icon chip — solid accent on white card */}
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 transition-transform duration-500 group-hover:scale-110 relative z-10 shadow-md"
                  style={{ background: accentHex }}
                >
                  <item.icon size={22} weight="duotone" className="text-white" />
                </div>

                {/* Pending counters */}
                {/* Leaves card badge:
                    • GM → pending leaves at GM stage.
                    • Admin/Dept-Mgr → org-wide pending leaves.
                    • Finance Mgr → pending clearances awaiting finance approval
                      (the Leaves card now hosts the Clearances tab for FMs). */}
                {item.label === 'leaves' && (
                  ((isGM && stats?.pending_gm_leaves > 0) && (
                    <span className={`absolute top-3 ${badgePos} bg-[#DC2626] text-white text-xs font-bold rounded-full min-w-[24px] h-6 flex items-center justify-center px-1.5 shadow-md z-20`} data-testid="pending-leaves-badge">{stats.pending_gm_leaves}</span>
                  ))
                  || (isFinanceMgr && stats?.pending_finance_clearances > 0 && (
                    <span className={`absolute top-3 ${badgePos} bg-[#DC2626] text-white text-xs font-bold rounded-full min-w-[24px] h-6 flex items-center justify-center px-1.5 shadow-md z-20`} data-testid="pending-leaves-badge">{stats.pending_finance_clearances}</span>
                  ))
                  || (!isGM && !isFinanceMgr && stats?.pending_leaves > 0 && (
                    <span className={`absolute top-3 ${badgePos} bg-[#DC2626] text-white text-xs font-bold rounded-full min-w-[24px] h-6 flex items-center justify-center px-1.5 shadow-md z-20`} data-testid="pending-leaves-badge">{stats.pending_leaves}</span>
                  ))
                )}
                {item.label === 'clearance_management' && stats?.pending_clearances > 0 && (
                  <span className={`absolute top-3 ${badgePos} bg-[#DC2626] text-white text-xs font-bold rounded-full min-w-[24px] h-6 flex items-center justify-center px-1.5 shadow-md z-20`} data-testid="pending-clearances-badge">{stats.pending_clearances}</span>
                )}
                {/* Standalone 'clearance' card — used by GM only now. The
                    Finance Manager's clearance access lives inside the
                    'leaves' card (badge above) since 2026-02. */}
                {item.label === 'clearance' && isGM && stats?.pending_gm_clearances > 0 && (
                  <span className={`absolute top-3 ${badgePos} bg-[#DC2626] text-white text-xs font-bold rounded-full min-w-[24px] h-6 flex items-center justify-center px-1.5 shadow-md z-20`} data-testid="pending-role-clearances-badge">
                    {stats.pending_gm_clearances}
                  </span>
                )}
                {/* Note: a "pending clearances" badge was previously rendered
                    on the `my_leaves` card, but that stat represents
                    organisation-wide clearances awaiting admin/finance/GM
                    review — semantically unrelated to "my leaves". The
                    clearance card (line above) already surfaces this count
                    for the right roles. Keeping this block removed to avoid
                    the red dot showing up on Finance Manager's إجازاتي. */}
                {item.label === 'dashboard' && !isAdmin && deptPendingCount > 0 && (
                  <span className={`absolute top-3 ${badgePos} bg-[#DC2626] text-white text-xs font-bold rounded-full min-w-[24px] h-6 flex items-center justify-center px-1.5 shadow-md z-20`} data-testid="dept-pending-badge">{deptPendingCount}</span>
                )}
                {item.label === 'requests_management' && stats?.pending_requests > 0 && (
                  <span className={`absolute top-3 ${badgePos} bg-[#DC2626] text-white text-xs font-bold rounded-full min-w-[24px] h-6 flex items-center justify-center px-1.5 shadow-md z-20`} data-testid="pending-requests-badge">{stats.pending_requests}</span>
                )}
                {item.label === 'my_requests' && stats?.my_open_requests > 0 && (
                  <span className={`absolute top-3 ${badgePos} bg-[#DC2626] text-white text-xs font-bold rounded-full min-w-[24px] h-6 flex items-center justify-center px-1.5 shadow-md z-20`} data-testid="my-open-requests-badge">{stats.my_open_requests}</span>
                )}
                {item.label === 'attendance' && isAdmin && stats?.pending_attendance_corrections > 0 && (
                  <span className={`absolute top-3 ${badgePos} bg-[#DC2626] text-white text-xs font-bold rounded-full min-w-[24px] h-6 flex items-center justify-center px-1.5 shadow-md z-20`} data-testid="pending-attendance-corrections-badge">{stats.pending_attendance_corrections}</span>
                )}

                <div className="mt-auto relative z-10">
                  <h3 className="text-base font-bold mb-1 text-[#1F2937]" style={{ animationDelay: `${index * 100}ms` }}>{t(item.label)}</h3>
                  <p className="text-[#6B7280] text-xs leading-relaxed">
                    {language === 'en' ? item.description_en : item.description_ar}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Team attendance card — auto-hides if the caller isn't a
            manager or if the admin toggle is off. Renders here for GMs,
            department managers, and any user who has direct reports. */}
        <div className="mt-6">
          <ManagerTeamAttendanceCard language={language} />
        </div>

        {/* Footer */}
        <div className="py-2 mt-auto">
          <p className="text-center text-sm text-[#655dc6]/60">
            © {new Date().getFullYear()} {t('app_name')}. All rights reserved.
          </p>
        </div>
      </main>

      {/* Inline sign-custody dialog — lets the employee acknowledge
          receipt right here on the home page without a trip to the
          dashboard. Submits the same /custody/{id}/employee-sign the
          dashboard uses. */}
      <Dialog open={!!signTarget} onOpenChange={(o) => !o && setSignTarget(null)}>
        <DialogContent dir={direction} className="max-w-md" data-testid="home-sign-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-700 flex items-center gap-2">
              <Package size={18} weight="duotone" />
              {language === 'ar' ? 'توقيع استلام العهدة' : 'Sign custody receipt'}
            </DialogTitle>
            <DialogDescription>
              {language === 'ar'
                ? 'يرجى مراجعة بيانات الصنف والتوقيع للإقرار بالاستلام.'
                : 'Review the asset details and sign to acknowledge receipt.'}
            </DialogDescription>
          </DialogHeader>
          {signTarget && (
            <div className="space-y-3">
              <div className="rounded-lg bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-800/50 p-3 text-sm">
                <div className="font-semibold text-[#262263] dark:text-amber-100">{signTarget.asset?.name || '—'}</div>
                {signTarget.asset?.serial_number && (
                  <div className="text-xs font-mono text-gray-600 dark:text-slate-300 mt-0.5">SN: {signTarget.asset.serial_number}</div>
                )}
                {signTarget.asset?.brand && (
                  <div className="text-xs text-gray-600 dark:text-slate-300">{signTarget.asset.brand} {signTarget.asset.model && `· ${signTarget.asset.model}`}</div>
                )}
              </div>
              <div>
                <label className="text-xs text-[#4B5563] dark:text-slate-300 font-semibold mb-1 block">
                  {language === 'ar' ? 'الاسم كامل كما تود طباعته' : 'Full name as it should appear'}
                </label>
                <input
                  type="text"
                  value={signName}
                  onChange={(e) => setSignName(e.target.value)}
                  className="w-full h-9 px-3 rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm"
                  placeholder={user?.name}
                  data-testid="home-sign-name"
                />
              </div>
              <div>
                <label className="text-xs text-[#4B5563] dark:text-slate-300 font-semibold mb-1 block">
                  {language === 'ar' ? 'وقّع هنا' : 'Sign below'}
                </label>
                <SignaturePad
                  ref={signPadRef}
                  onChange={setSignEmpty}
                  height={140}
                  data-testid="home-sign-pad"
                />
                <button
                  type="button"
                  onClick={() => { signPadRef.current?.clear(); setSignEmpty(true); }}
                  className="mt-1 text-xs text-gray-500 hover:text-gray-700 dark:text-slate-400"
                  data-testid="home-sign-clear"
                >
                  {language === 'ar' ? 'مسح التوقيع' : 'Clear signature'}
                </button>
              </div>
              <p className="text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-800/50 rounded px-3 py-2">
                {language === 'ar'
                  ? 'بتوقيعك أدناه، تقر باستلام الصنف المذكور بحالته الفعلية وتتعهد بالمحافظة عليه وإعادته عند الطلب.'
                  : 'By signing, you acknowledge receipt of the asset in its stated condition and undertake to safeguard and return it on request.'}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignTarget(null)} disabled={savingSign} data-testid="home-sign-cancel">
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={submitInlineSignature}
              disabled={savingSign || signEmpty}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="home-sign-submit"
            >
              {savingSign ? '…' : (language === 'ar' ? 'توقيع واعتماد' : 'Sign & confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
